<template>
  <section v-if="displayItems && displayItems.length > 0" class="recommended-slider-section mt-10 sm:mt-14">
    <!-- Header -->
    <div class="flex items-center justify-between gap-4 mb-4 sm:mb-5">
      <div>
        <div v-if="badge" class="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full bg-blue-500/10 text-blue-500 text-[10px] sm:text-[11px] font-black uppercase tracking-wider mb-1">
          <span>{{ badge }}</span>
        </div>
        <h2 class="text-xl sm:text-2xl font-black text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
          <span>{{ title }}</span>
          <span class="text-xs font-bold text-slate-400 font-mono">({{ displayItems.length }})</span>
        </h2>
        <p v-if="subtitle" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          {{ subtitle }}
        </p>
      </div>

      <!-- Controls & Actions -->
      <div class="flex items-center gap-2">
        <button
          v-if="showClear"
          @click="$emit('clear')"
          class="text-xs font-bold text-slate-400 hover:text-rose-500 transition-colors mr-2 flex items-center gap-1"
          title="Clear browsing history"
        >
          <span>✕</span>
          <span class="hidden sm:inline">Clear History</span>
        </button>

        <!-- Slider Arrows -->
        <button
          @click="scrollSlider(-1)"
          class="w-8 h-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-xs flex items-center justify-center transition-all shadow-sm active:scale-90"
          title="Slide Left"
        >
          ❮
        </button>
        <button
          @click="scrollSlider(1)"
          class="w-8 h-8 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:bg-slate-100 dark:hover:bg-slate-800 text-slate-700 dark:text-slate-300 font-black text-xs flex items-center justify-center transition-all shadow-sm active:scale-90"
          title="Slide Right"
        >
          ❯
        </button>
      </div>
    </div>

    <!-- Sliding Row -->
    <div
      ref="sliderRef"
      class="flex gap-3 sm:gap-4 overflow-x-auto scrollbar-none scroll-smooth pb-3 pt-1 snap-x snap-mandatory"
    >
      <article
        v-for="item in displayItems"
        :key="item.type ? `${item.type}-${item.id}` : item.id"
        class="w-40 sm:w-48 md:w-52 shrink-0 snap-start group relative bg-white dark:bg-slate-900/90 border border-slate-200/80 dark:border-slate-800 rounded-2xl p-2.5 sm:p-3.5 shadow-sm hover:shadow-xl hover:border-blue-500/40 transition-all duration-300 flex flex-col justify-between overflow-hidden"
      >
        <!-- Top Gradient Line on Hover -->
        <div class="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-blue-500 via-indigo-500 to-amber-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>

        <div>
          <!-- Thumbnail Frame -->
          <router-link
            :to="getProductLink(item)"
            class="block relative aspect-square rounded-2xl bg-slate-50 dark:bg-slate-800/80 border border-slate-200/60 dark:border-slate-700/60 overflow-hidden mb-2.5 cursor-pointer group-hover:border-blue-500/30 transition-colors p-3"
          >
            <img
              :src="resolveImageUrl(item)"
              :alt="item.title || item.libelle"
              @error="(e) => handleImageError(e, item)"
              loading="lazy"
              class="w-full h-full object-contain transition-transform duration-500 group-hover:scale-105"
            />

            <!-- Category / Tag Floating Badge -->
            <div
              v-if="item.brand || item.category_name"
              class="absolute top-2 left-2 px-2 py-0.5 text-[8px] sm:text-[9px] font-black rounded-md bg-slate-900/85 text-blue-400 border border-blue-500/20 backdrop-blur-md shadow-sm"
            >
              {{ item.brand || item.category_name }}
            </div>
          </router-link>

          <!-- Title -->
          <router-link
            :to="getProductLink(item)"
            class="block text-xs sm:text-sm font-bold text-slate-900 dark:text-white line-clamp-1 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors leading-tight mb-1"
            :title="item.title || item.libelle"
          >
            {{ item.title || item.libelle }}
          </router-link>

          <!-- Star Rating -->
          <div class="flex items-center gap-1 my-1">
            <StarRating
              :rating="Number(item.average_rating || 0)"
              size="xs"
            />
            <span class="text-[10px] font-black text-amber-500">
              {{ Number(item.average_rating || 0).toFixed(1) }}
            </span>
            <span v-if="item.ratings_count > 0" class="text-[9px] text-slate-400">
              ({{ item.ratings_count }})
            </span>
          </div>
        </div>

        <!-- Footer / Price Row -->
        <div class="pt-2 border-t border-slate-100 dark:border-slate-800/80 mt-2 flex items-center justify-between gap-1">
          <div>
            <span class="text-[9px] text-slate-400 block uppercase font-bold tracking-wider">
              {{ getPriceInfo(item).label }}
            </span>
            <span class="text-xs sm:text-sm font-black text-blue-600 dark:text-blue-400 block leading-tight">
              {{ getPriceInfo(item).value }}
            </span>
          </div>

          <router-link
            :to="getProductLink(item)"
            class="px-2.5 py-1.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-extrabold text-[10px] uppercase tracking-wider transition-all shadow-sm shrink-0"
          >
            View
          </router-link>
        </div>
      </article>
    </div>
  </section>
</template>

<script>
import { ref, computed } from 'vue';
import { useDataStore } from '@/stores/dataStore.js';
import { slugify } from '@/utils/slugify.js';
import StarRating from '@/components/ui/StarRating.vue';

export default {
  name: 'RecommendedProductsSlider',
  components: {
    StarRating
  },
  props: {
    title: {
      type: String,
      default: 'Recommended For You'
    },
    subtitle: {
      type: String,
      default: ''
    },
    badge: {
      type: String,
      default: ''
    },
    items: {
      type: Array,
      default: () => []
    },
    showClear: {
      type: Boolean,
      default: false
    }
  },
  emits: ['clear'],
  setup(props) {
    const sliderRef = ref(null);
    const dataStore = useDataStore();

    const RECHARGE_SUBCAT_IDS = [157, 158, 159, 160, 162, 163];
    const RECHARGE_CAT_IDS = [33, 34, 35];

    const displayItems = computed(() => {
      if (!props.items || !Array.isArray(props.items)) return [];
      return props.items.filter(item => {
        if (!item) return false;
        if (item.type === 'physical') return true;
        const sId = parseInt(item.id);
        const cId = parseInt(item.categorie_id || item.category_id);
        return !RECHARGE_SUBCAT_IDS.includes(sId) && !RECHARGE_CAT_IDS.includes(cId);
      });
    });

    const scrollSlider = (direction) => {
      if (!sliderRef.value) return;
      const scrollAmount = 350 * direction;
      sliderRef.value.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    };

    const getProductLink = (item) => {
      if (!item) return '#';
      if (item.type === 'physical') {
        return `/physical-product/${item.slug || item.id}`;
      }
      // 1. Try canonical subcategory path from dataStore
      if (item.id) {
        const canonical = dataStore.getSubcategorySlugPath(item.id);
        if (canonical) return canonical;
      }
      // 2. Direct category_slug and slug
      const catSlug = slugify(item.category_slug || item.category_name || 'subscriptions');
      const subSlug = slugify(item.slug || item.libelle || item.title);
      return `/${catSlug}/${subSlug}`;
    };

    const resolveImageUrl = (item) => {
      if (!item) return '/placeholder-image.png';

      // 1. Direct image_url if provided
      let raw = item.image_url || item.image || item.main_image || '';
      if (!raw || typeof raw !== 'string') return '/placeholder-image.png';
      raw = raw.trim();

      const base = (import.meta.env.VITE_IMAGE_URL || 'https://api.eskrag.com').replace(/\/+$/, '');

      // 2. Subcategory relative path or legacy localhost URL
      if (raw.includes('assests/Souscategorie/')) {
        const relative = raw.substring(raw.indexOf('assests/Souscategorie/'));
        return `${base}/${relative}`;
      }

      // 3. Physical product relative path or legacy localhost URL
      if (raw.includes('images/physical_products/')) {
        const relative = raw.substring(raw.indexOf('images/physical_products/'));
        return `${base}/${relative}`;
      }

      // 4. Fully qualified external / internal URL
      if (raw.startsWith('http://') || raw.startsWith('https://')) {
        if (typeof window !== 'undefined' && window.location.protocol === 'https:' && raw.startsWith('http://')) {
          return raw.replace('http://', 'https://');
        }
        return raw;
      }

      // Clean leading slash
      const cleanPath = raw.replace(/^\/+/, '');

      // 4. If path already has directory prefix
      if (cleanPath.startsWith('assests/') || cleanPath.startsWith('images/') || cleanPath.startsWith('storage/')) {
        return `${base}/${cleanPath}`;
      }

      // 5. Physical product filename
      if (item.type === 'physical') {
        return `${base}/images/physical_products/${cleanPath}`;
      }

      // 6. Subcategory images live in assests/Souscategorie/ on the backend
      return `${base}/assests/Souscategorie/${cleanPath}`;
    };

    const handleImageError = (event, item) => {
      const target = event.target;
      const base = (import.meta.env.VITE_IMAGE_URL || 'https://api.eskrag.com').replace(/\/+$/, '');
      const raw = item?.image || item?.main_image || '';

      if (raw && !target.dataset.triedFallback) {
        target.dataset.triedFallback = '1';
        // Try direct /images/ or /assests/
        const clean = raw.replace(/^\/+/, '');
        if (!target.src.includes('assests/Souscategorie')) {
          target.src = `${base}/assests/Souscategorie/${clean}`;
          return;
        }
        target.src = `${base}/images/${clean}`;
        return;
      }

      target.src = '/placeholder-image.png';
    };

    const getPriceInfo = (item) => {
      // Physical product
      if (item.type === 'physical') {
        const p = Number(item.price);
        if (!isNaN(p) && p > 0) {
          return { label: 'Price', value: `${p.toLocaleString()} DZD` };
        }
        return { label: 'Hardware', value: 'View Details' };
      }

      // Digital Subcategory:
      // Priority 1: In Algerian DA mode, use min_price_da directly
      const isDA = dataStore.shouldUseDAPricing || dataStore.user?.is_da === 2 || dataStore.user?.is_da === '2';
      if (isDA && item.min_price_da && Number(item.min_price_da) > 0) {
        return { label: 'From', value: `${Number(item.min_price_da).toLocaleString()} DA` };
      }

      // Priority 2: Use converted price with store's currency symbol
      const rawPrice = item.min_price ?? item.price;
      if (rawPrice !== null && rawPrice !== undefined && Number(rawPrice) > 0) {
        const converted = dataStore.convertPrice(rawPrice);
        const symbol = dataStore.currencySymbol || 'DA';
        return { label: 'From', value: `${converted} ${symbol}` };
      }

      // Priority 3: Fallback to min_price_da if available
      if (item.min_price_da && Number(item.min_price_da) > 0) {
        return { label: 'From', value: `${Number(item.min_price_da).toLocaleString()} DA` };
      }

      return { label: 'Instant', value: 'Explore Offers' };
    };

    return {
      sliderRef,
      displayItems,
      scrollSlider,
      getProductLink,
      resolveImageUrl,
      handleImageError,
      getPriceInfo
    };
  }
};
</script>

<style scoped>
.scrollbar-none::-webkit-scrollbar {
  display: none;
}
</style>
